import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useSelectedStore, useUpdateTargetSite } from '@web/app/shared';
import { consumePendingRedirect } from '@web/app/shared/hooks/usePendingRedirect';
import axios, { AxiosError } from 'axios';

import { useLoader } from '@codes/loader';
import { ownerQueryKeys, useOwner } from '@codes/owner';
import { projectsKeys } from '@codes/project';
import { toast } from '@codes/ui-kit';
import { isProdEnvironment, useProfile, useWebCoreStore } from '@codes/web-core';
import { useMyWorkspaces, useWorkspace, workspacesKeys } from '@codes/workspace';

export const useWorkspaceService = () => {
    const isProd = isProdEnvironment();
    const navigate = useNavigate();

    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { setIsLoading } = useLoader();
    const updateTargetSite = useUpdateTargetSite();

    const { fetchAndSetOwner } = useOwner();
    const { loadProfile } = useProfile();

    const { workspace, setProject, setWorkspace } = useSelectedStore();

    const workspaceFromServerQueryData = useWorkspace('0');
    const workspaceFromServer = workspaceFromServerQueryData.data;

    const myWorkspacesQueryResult = useMyWorkspaces();

    const { profile } = useWebCoreStore();

    const myWorkspaces = myWorkspacesQueryResult.data?.data ?? [];
    const areMyWorkspacesEmpty = myWorkspacesQueryResult.data?.total === 0;

    const isApprovedWorkspace =
        (isProd && workspaceFromServer?.name === 'default') || !!workspaceFromServer?.approvedAt;

    const replaceWorkspace = async (workspaceId: string, siteId: string) => {
        const { workspace: currentWorkspace } = useSelectedStore.getState();
        const isChangingWorkspace = currentWorkspace && currentWorkspace.siteId !== siteId;

        setIsLoading(true);
        try {
            await updateTargetSite(siteId);
            const [owner] = await Promise.all([fetchAndSetOwner(), loadProfile()]);
            queryClient.removeQueries({ queryKey: ownerQueryKeys.list({}), exact: false });
            queryClient.removeQueries({ queryKey: projectsKeys.lists(), exact: false });
            queryClient.removeQueries({ queryKey: workspacesKeys.detail('0') });

            setWorkspace(owner?.$workspace || null);

            if (isChangingWorkspace) {
                const lastProject = useSelectedStore.getState().getLastProject(workspaceId);
                setProject(lastProject ?? null);
            }

            const redirectUrl = consumePendingRedirect(workspaceId);
            if (redirectUrl) {
                navigate(redirectUrl, { replace: true });
            } else {
                const { project: restoredProject } = useSelectedStore.getState();
                const projectPath = restoredProject?.id ? `/p/${restoredProject.id}` : '';
                navigate(`/w/${workspaceId}${projectPath}`, { replace: true });
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                toast({ description: e.response?.data });
            } else {
                toast({ description: t('error') });
            }
            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    const isOwner = ((profile?.roles ?? []) as string[]).includes('owner');

    useEffect(() => {
        if (!workspaceFromServer) {
            return;
        }

        if (!workspace || workspace.id !== workspaceFromServer.id || workspace.siteId !== workspaceFromServer.siteId) {
            setWorkspace(workspaceFromServer);
            return;
        }

        const hasRelevantChanges =
            workspace.geminiApiKey !== workspaceFromServer.geminiApiKey ||
            workspace.name !== workspaceFromServer.name ||
            workspace.regionId !== workspaceFromServer.regionId ||
            workspace.approvedAt !== workspaceFromServer.approvedAt;

        if (hasRelevantChanges) {
            setWorkspace(workspaceFromServer);
        }
    }, [workspaceFromServer, workspace, setWorkspace]);

    useEffect(() => {
        if (!workspaceFromServerQueryData.error) {
            return;
        }

        const axiosError = workspaceFromServerQueryData.error as AxiosError<string>;
        toast({ description: axiosError.response?.data || 'unknown error' });

        setWorkspace(null);
        setProject(null);
    }, [workspaceFromServerQueryData.error]);

    return {
        myWorkspacesQueryResult,
        myWorkspaces,
        areMyWorkspacesEmpty,
        isApprovedWorkspace,
        workspaceFromServer,
        workspaceFromServerQueryData,
        replaceWorkspace,
        isOwner,
    };
};
